/**
 * Cliente HTTP — Instancia de Axios con auth, reintentos ante red inestable
 * y manejo de errores críticos.
 *
 * Reintentos:
 *  - GET: hasta 3 intentos ante error de red, timeout o 408/429/502/503/504.
 *  - POST/PUT/PATCH: solo 1 reintento si NO hubo respuesta del servidor (fallo
 *    de red puro); si el servidor respondió, NO se reintenta (evita duplicar
 *    cobros u órdenes).
 *
 * Nada de esto “garantiza” AWS: cortes del proveedor, DNS o TLS pueden fallar.
 * Reduce la necesidad de recargar la página cuando el fallo es transitorio.
 *
 * Exporta: default export
 */
import { logger } from "@/lib/logger";
import axios, {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "@/store/authStore";
import toast from "react-hot-toast";

/**
 * En desarrollo, MSW intercepta solo el mismo origen que sirve la SPA (Vite).
 * Si `VITE_API_URL` apunta a otro host (p. ej. `http://localhost:5000/api`),
 * las peticiones salen del worker y el backend real suele responder 404 en
 * rutas solo mockeadas (`/caja/abrir`, etc.). Forzamos `/api` salvo opt-out.
 */
function resolveApiBaseUrl(): string {
  const useRemote =
    import.meta.env.VITE_DEV_USE_REMOTE_API === "true" ||
    import.meta.env.VITE_ENABLE_MSW === "false";

  /** Dev + MSW: siempre mismo origen Vite y prefijo /api (handlers en mocks). */
  if (import.meta.env.DEV && !useRemote) {
    return "/api";
  }

  const raw = (import.meta.env.VITE_API_URL || "/api").trim() || "/api";
  if (!import.meta.env.DEV) return raw;
  if (!raw.startsWith("http://") && !raw.startsWith("https://")) {
    return raw.startsWith("/") ? raw : "/api";
  }
  if (typeof window === "undefined" || !window.location?.origin) return raw;
  try {
    if (new URL(raw).origin !== window.location.origin) {
      return "/api";
    }
    const path = new URL(raw).pathname.replace(/\/$/, "") || "";
    if (path === "" || path === "/") return "/api";
    if (path === "/api" || path.startsWith("/api/")) return raw;
    return "/api";
  } catch {
    return raw;
  }
}

const API_URL = resolveApiBaseUrl();

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
  },
});

type RetryConfig = InternalAxiosRequestConfig & {
  __retryCount?: number;
  __mswReviveCount?: number;
};

const sleep = (ms: number): Promise<unknown> =>
  new Promise((r) => setTimeout(r, ms));

/**
 * ¿MSW (mock local) aún no intercepta? Durante ese "calentamiento" (1–3s tras
 * cada carga en el WebView del APK), las peticiones /api/* se irían a la red
 * real y fallarían. Reintentamos los GET hasta que el worker esté activo.
 */
function isMswWarmingUp(): boolean {
  const w = window as Window & {
    __altenotMswEnabled?: boolean;
    __altenotMswReady?: boolean;
  };
  return !!w.__altenotMswEnabled && !w.__altenotMswReady;
}

const MSW_WARMUP_MAX_RETRIES = 20;
const MSW_WARMUP_DELAY_MS = 400;

/**
 * Auto-recuperación de MSW: bajo navegación rápida, el WebView puede MATAR el
 * Service Worker del mock por presión de memoria. Sin SW, todo /api/* se va a la
 * red real y falla. Si detectamos ese fallo de red con MSW activo, re-registramos
 * el worker una vez (con tope de frecuencia) para que vuelva a interceptar.
 */
const MSW_REVIVE_MIN_GAP_MS = 5000;
let lastMswReviveTs = 0;

async function tryReviveMsw(): Promise<boolean> {
  const w = window as Window & {
    __altenotMswEnabled?: boolean;
    __altenotMswReady?: boolean;
  };
  if (!w.__altenotMswEnabled) return false;

  const now = Date.now();
  if (now - lastMswReviveTs < MSW_REVIVE_MIN_GAP_MS) return false;
  lastMswReviveTs = now;

  try {
    w.__altenotMswReady = false; // entra en warmup mientras re-registra
    const { worker } = await import("@/mocks/browser");
    await worker.start({
      onUnhandledRequest: "bypass",
      quiet: true,
      waitUntilReady: false,
      serviceWorker: {
        url: "/mockServiceWorker.js",
        options: { scope: "/" },
      },
    });
    w.__altenotMswReady = true;
    logger.warn("[MSW] Service Worker re-registrado tras caída.");
    return true;
  } catch (err) {
    w.__altenotMswReady = true;
    logger.warn("[MSW] No se pudo re-registrar el worker:", err);
    return false;
  }
}

/**
 * ¿La sesión actual es demo/mock? (token mock o que contiene "demo").
 * Lee del store y, como respaldo tras una recarga donde el store aún no
 * hidrató, del localStorage de sesión. En demo NUNCA se expulsa ni se
 * fuerza recarga (evita bucles de "recarga infinita" en el APK/WebView).
 */
function isDemoSession(): boolean {
  const storeToken = useAuthStore.getState().token || "";
  const token =
    storeToken ||
    (() => {
      try {
        return localStorage.getItem("token") || "";
      } catch {
        return "";
      }
    })();
  return token.startsWith("mock-token") || token.includes("demo");
}

/**
 * Cortacircuitos de redirección a /login. Evita el bucle de recargas: si ya
 * redirigimos hace muy poco, NO volvemos a hacerlo (rompe el ciclo
 * 403 → replace("/login") → recarga → 403 → …).
 */
const LOGIN_REDIRECT_TS_KEY = "altenot_login_redirect_ts";
const LOGIN_REDIRECT_MIN_GAP_MS = 8000;

function redirectToLoginOnce(): void {
  try {
    const last = Number(sessionStorage.getItem(LOGIN_REDIRECT_TS_KEY) || "0");
    const now = Date.now();
    if (now - last < LOGIN_REDIRECT_MIN_GAP_MS) {
      logger.warn(
        "[auth] Redirección a /login omitida (cortacircuitos anti-bucle).",
      );
      return;
    }
    sessionStorage.setItem(LOGIN_REDIRECT_TS_KEY, String(now));
  } catch {
    /* si sessionStorage falla, seguimos con la redirección */
  }
  window.location.replace("/login");
}

function shouldRetryRequest(error: AxiosError, config: RetryConfig): boolean {
  const method = (config.method || "get").toLowerCase();
  const n = config.__retryCount ?? 0;
  const noResponse = !error.response;

  if (method === "get") {
    // Calentamiento de MSW: reintentar GET ante CUALQUIER fallo (incluye 404/401/403
    // de la red real) hasta que el mock intercepte. Rompe el bucle de recargas.
    if (isMswWarmingUp() && n < MSW_WARMUP_MAX_RETRIES) return true;
    if (n >= 3) return false;
    if (noResponse) return true;
    const s = error.response?.status;
    if (s === undefined) return false;
    if (s === 401 || s === 403 || s === 400) return false;
    return s === 408 || s === 429 || s === 502 || s === 503 || s === 504;
  }

  /** Mutaciones: solo un reintento ante fallo de red (nunca si ya hubo HTTP). */
  if (["post", "put", "patch", "delete"].includes(method)) {
    if (n >= 1) return false;
    return noResponse;
  }

  return false;
}

api.interceptors.request.use(
  (config): InternalAxiosRequestConfig => {
    const token = useAuthStore.getState().token;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: unknown): Promise<never> => {
    return Promise.reject(error);
  },
);

/**
 * Durante el calentamiento de MSW, Vite (o el servidor local de Capacitor en
 * Android) responde 200 con el HTML del índice SPA para rutas /api/* que aún
 * no intercepta el Service Worker del mock. Detectamos ese caso por
 * content-type: NUNCA es una respuesta JSON válida de /api/*.
 */
function isHtmlSpaFallback(response: {
  status: number;
  headers: Record<string, unknown>;
  data: unknown;
}): boolean {
  const ct = String(response.headers?.["content-type"] ?? "");
  if (!ct.includes("text/html")) return false;
  return true;
}

/**
 * BUG RAÍZ CORREGIDO (jul-2026): cuando se agotaban los reintentos de
 * calentamiento (`MSW_WARMUP_MAX_RETRIES`) y la respuesta seguía siendo el
 * HTML de la SPA, el interceptor devolvía esa respuesta como si fuera un 200
 * legítimo. `unwrapApiData` no encuentra un objeto `data` en un string HTML y
 * resuelve el fallback vacío (`{ puntos: [] }`, `{ ventas: [] }`, …), que los
 * hooks de Caja aplicaban tal cual sobre el estado ya cargado: pantalla
 * "Sin puntos" y ventas que "desaparecían" del historial aunque siguieran
 * intactas en Auditoría y en el storage local ACID. Los propios hooks (y
 * `caja.service.ts`) YA tienen fallback a localStorage en su `catch`; el
 * problema era que esta rama nunca llegaba a rechazar la promesa. Ahora, si
 * agotamos los reintentos (o seguimos recibiendo HTML fuera de la ventana de
 * calentamiento) intentamos re-registrar el Service Worker una vez y, si no
 * ayuda, RECHAZAMOS explícitamente para que cada caller conserve su último
 * estado válido en vez de vaciar la pantalla con datos rotos.
 */
async function rejectOrReviveHtmlFallback(
  config: RetryConfig,
): Promise<AxiosResponse> {
  const w = window as Window & { __altenotMswEnabled?: boolean };
  const reviveCount = config.__mswReviveCount ?? 0;
  if (w.__altenotMswEnabled && !isMswWarmingUp() && reviveCount < 1) {
    config.__mswReviveCount = reviveCount + 1;
    const revived = await tryReviveMsw();
    if (revived) {
      config.__retryCount = 0;
      await sleep(MSW_WARMUP_DELAY_MS);
      return api.request(config);
    }
  }
  throw Object.assign(
    new Error("MSW no interceptó la petición (fallback HTML de la SPA)."),
    { config, isAxiosError: true, __mswHtmlFallback: true },
  );
}

api.interceptors.response.use(
  async (response) => {
    const config = response.config as RetryConfig;
    const method = (config.method || "get").toLowerCase();
    const htmlFallback =
      method === "get" &&
      isHtmlSpaFallback(
        response as {
          status: number;
          headers: Record<string, unknown>;
          data: unknown;
        },
      );

    if (htmlFallback) {
      if (
        isMswWarmingUp() &&
        (config.__retryCount ?? 0) < MSW_WARMUP_MAX_RETRIES
      ) {
        config.__retryCount = (config.__retryCount ?? 0) + 1;
        await sleep(MSW_WARMUP_DELAY_MS);
        return api.request(config);
      }
      return rejectOrReviveHtmlFallback(config);
    }

    return response;
  },
  async (error: AxiosError<{ message?: string; error?: string }>) => {
    const config = error.config as RetryConfig | undefined;

    if (config && shouldRetryRequest(error, config)) {
      config.__retryCount = (config.__retryCount ?? 0) + 1;
      // Durante el calentamiento de MSW usamos un retardo corto y fijo
      // (el worker suele activarse en 1–3s); fuera de eso, backoff normal.
      const delay = isMswWarmingUp()
        ? MSW_WARMUP_DELAY_MS
        : 350 * config.__retryCount;
      await sleep(delay);
      return api.request(config);
    }

    // Auto-recuperación: GET con error de red y MSW activo → el SW pudo morir.
    // Re-registramos el worker una vez y reintentamos antes de dar por fallido.
    if (config && !error.response) {
      const method = (config.method || "get").toLowerCase();
      const w = window as Window & { __altenotMswEnabled?: boolean };
      const reviveCount = config.__mswReviveCount ?? 0;
      if (
        method === "get" &&
        w.__altenotMswEnabled &&
        !isMswWarmingUp() &&
        reviveCount < 1
      ) {
        config.__mswReviveCount = reviveCount + 1;
        const revived = await tryReviveMsw();
        if (revived) {
          config.__retryCount = 0;
          await sleep(MSW_WARMUP_DELAY_MS);
          return api.request(config);
        }
      }
    }

    if (!error.response) {
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    const url = error.config?.url || "";

    if (status === 401 && !url.includes("/login")) {
      // Demo/mock: jamás expulsar ni recargar (rompe bucles en el APK).
      if (isDemoSession()) {
        logger.warn("⚠️ 401 en Modo Demo - Ignorando Logout automático");
        return Promise.reject(error);
      }

      useAuthStore.getState().logout();
      redirectToLoginOnce();
      return Promise.reject(error);
    }

    if (status === 403) {
      const message = data?.message || "";

      if (
        url.includes("/auth/me") ||
        message.includes("desactivada") ||
        message.includes("desactivado") ||
        message.includes("Contacta al administrador")
      ) {
        // Demo/mock: el 403 suele venir del backend real cuando MSW aún no
        // intercepta tras una recarga. NO expulsar ni recargar en demo.
        if (isDemoSession()) {
          logger.warn("⚠️ 403 en Modo Demo - Ignorando expulsión automática");
          return Promise.reject(error);
        }

        logger.log("🔒 Usuario desactivado detectado - Expulsando...");

        const authStore = useAuthStore.getState();
        authStore.logout();

        toast.error("Tu cuenta ha sido desactivada por un administrador.", {
          id: "user-deactivated",
          duration: 5000,
          style: {
            background: "#FEE2E2",
            color: "#991B1B",
            fontWeight: 600,
          },
        });

        setTimeout(() => {
          redirectToLoginOnce();
        }, 200);

        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
